import React, { useState, useEffect} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Checkbox,
} from "@mui/material";
import api from "../../api";

const defaultColumns = { id: "ID", name: "Name" };

const TutorListPage =  ({
  title,
  endpoint,
  columns = defaultColumns,
  transformRow = (row, idx, meta) => row,
  perPageKey = "per_page",
  courseIdFilter = false,
  studentFilters = false,
  renderActions,
  disablePagination = false,
  emptyMessage = "No records found",
  // Optional row selection
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  getRowId = (row) => row.id,
  isRowSelectable = () => true,
  renderSelectionToolbar,
  onDataChange,
  refreshTrigger = 0,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [courseId, setCourseId] = useState("");
  const [courses, setCourses] = useState([]);
  const [nameQuery, setNameQuery] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [schoolYearId, setSchoolYearId] = useState("");
  const [schoolYears, setSchoolYears] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (!disablePagination) {
        params[perPageKey] = perPage;
        params.page = page + 1;
      }
      if (courseIdFilter && courseId) params.course_id = courseId;
      if (studentFilters) {
        if (appliedName.trim()) params.search = appliedName.trim();
        if (schoolYearId) params.school_year_id = schoolYearId;
      }
      const res = await api.get(endpoint, { params });
      if (res.data?.success) {
        const d = res.data.data;
        if (Array.isArray(d)) {
          setData(d);
          setTotal(d.length);
          onDataChange?.(d);
        } else if (d?.data !== undefined) {
          const rows = Array.isArray(d.data) ? d.data : [];
          setData(Array.isArray(d.data) ? d.data : []);
          setTotal(Number(d.total) ?? 0);
          onDataChange?.(rows);
        } else {
          const rows = Array.isArray(d) ? d : [];
          setData(Array.isArray(d) ? d : []);
          setTotal(Array.isArray(d) ? d.length : 0);
          onDataChange?.(rows);
        }
      } else {
        setError("Failed to load data");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseIdFilter && endpoint === "tutor/students") {
      api.get("tutor/courses").then((r) => {
          if (r.data?.success) setCourses(r.data.data || []);
        }).catch(() => setCourses([]));
    }
  }, [courseIdFilter, endpoint]);

  useEffect(() => {
    if (!studentFilters) return;
    api
      .get("common/data", { params: { param: "SchoolYears" } })
      .then((r) => {
        const list = r.data?.data || r.data || [];
        setSchoolYears(Array.isArray(list) ? list : []);
      })
      .catch(() => setSchoolYears([]));
  }, [studentFilters]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    page,
    perPage,
    courseId,
    appliedName,
    schoolYearId,
    disablePagination,
    refreshTrigger,
  ]);

  const applyNameFilter = () => {
    setPage(0);
    setAppliedName(nameQuery.trim());
  };

  const clearFilters = () => {
    setCourseId("");
    setNameQuery("");
    setAppliedName("");
    setSchoolYearId("");
    setPage(0);
  };

  if (loading && !data.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedSet = new Set(selectedRows);

  const selectableRows = data.filter((row) => isRowSelectable(row));
  const allCurrentPageSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedSet.has(getRowId(row)));

  const someCurrentPageSelected =
    selectableRows.some((row) => selectedSet.has(getRowId(row))) &&
    !allCurrentPageSelected;

  const handleSelectRow = (row) => {
    if (!isRowSelectable(row)) return;

    const id = getRowId(row);
    const next = new Set(selectedSet);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    onSelectionChange?.(Array.from(next));
  };

  const handleSelectAllCurrentPage = () => {
    const next = new Set(selectedSet);

    if (allCurrentPageSelected) {
      selectableRows.forEach((row) => {
        next.delete(getRowId(row));
      });
    } else {
      selectableRows.forEach((row) => {
        next.add(getRowId(row));
      });
    }

    onSelectionChange?.(Array.from(next));
  };

  const colEntries = Object.entries(columns);
  const hasRenderActions = typeof renderActions === "function";
  const showFilters = courseIdFilter || studentFilters;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {title}
      </Typography>
      {selectable && renderSelectionToolbar?.()}
      {showFilters && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2, flexWrap: "wrap" }}
        >
          {courseIdFilter && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Course</InputLabel>
              <Select
                value={courseId}
                label="Course"
                onChange={(e) => {
                  setCourseId(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {studentFilters && (
            <>
              <TextField
                size="small"
                label="Student name"
                placeholder="Search name or email"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyNameFilter();
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>School year</InputLabel>
                <Select
                  value={schoolYearId}
                  label="School year"
                  onChange={(e) => {
                    setSchoolYearId(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {schoolYears.map((y) => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" size="small" onClick={applyNameFilter}>
                Search
              </Button>
              <Button variant="outlined" size="small" onClick={clearFilters}>
                Clear
              </Button>
            </>
          )}
        </Stack>
      )}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allCurrentPageSelected}
                    indeterminate={someCurrentPageSelected}
                    onChange={handleSelectAllCurrentPage}
                    disabled={selectableRows.length === 0}
                  />
                </TableCell>
              )}

              {colEntries.map(([key, label]) => (
                <TableCell key={key} sx={{ fontWeight: 600 }}>
                  {label}
                </TableCell>
              ))}
              {hasRenderActions && (
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colEntries.length + (hasRenderActions ? 1 : 0) + (selectable ? 1 : 0)} align="center">
                  {loading ? "Loading…" : emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => {
                const r = transformRow(row, idx, { page, perPage });
                return (
                  <TableRow key={r.id ?? row.id ?? idx}>
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSet.has(getRowId(row))}
                          disabled={!isRowSelectable(row)}
                          onChange={() => handleSelectRow(row)}
                        />
                      </TableCell>
                    )}

                    {colEntries.map(([key]) => (
                      <TableCell key={key}>
                        {key === "no" ? page * perPage + idx + 1 : (r[key] ?? row[key] ?? "-")}
                      </TableCell>
                    ))}

                    {hasRenderActions && (
                      <TableCell>{renderActions(row)}</TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!disablePagination && total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 15, 25]}
        />
      )}
    </Box>
  );
};

export default TutorListPage;
